/**
 * LabelEditor — 라벨 칩 + 입력칸(+ 사전 후보). 문제 편집기(ProblemViewTab)와 OCR 검수 창(OcrReviewModal)이 같은 얼굴을 쓴다.
 *
 * 틀은 **ConceptPicker 를 그대로 따른다**(오너 결정 2026-09-13 D4 — 개념 고르기는 ConceptPicker 가 정본).
 * 두 편집기에서 개념 칸과 라벨 칸이 위아래로 붙어 서므로, 칩 상자·입력칸·후보 목록의 값이 같아야 한 층위로 읽힌다.
 * 칩의 색은 종전 검수 창 라벨 칩 그대로다 — 회색+자물쇠 = 여기서 못 뗌 · 하늘색+× = 뗄 수 있음.
 * 색만으로 나르지 않는다: 자물쇠 아이콘과 title 이 같은 것을 다시 말한다.
 *
 * 무엇을 넣고 빼는지는 **부르는 쪽이 정한다** — 이 부품은 얼굴과 키만 쥔다.
 *   · OCR 은 이름(문자열)을 모아 extra_labels 로 보낸다 — 사전 후보가 없다(suggest 를 안 준다).
 *   · 문제 편집기는 사전 번호(label_uuid)로 붙이고 뗀다 — suggest 로 사전을 찾는다.
 * 입력칸 값은 부르는 쪽이 쥔다(inputValue) — Enter 를 잊고 [저장]을 눌러도 친 글자가 사라지지 않게
 * 저장 쪽이 읽어야 해서다(검수 창 effectiveLabels 의 관용).
 *
 * @param items          [{ key, text, locked?, isNew?, hint? }]
 * @param onRemove       (key) — 잠긴 칩에는 × 가 없다
 * @param inputValue     입력칸 값
 * @param onInputChange  (value)
 * @param onCommit       Enter — 친 글자를 넣는다(가르는 규칙은 부르는 쪽)
 * @param suggest        (q) => Promise<[{ label_uuid, label_name, problem_count }]> — 없으면 후보 목록이 없다
 * @param onPick         (item) — 후보를 눌렀을 때
 * @param busy           저장 중 — 칩 떼기·입력·고르기를 잠근다(칸은 그대로 선다)
 */
import { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';

const SUGGEST_DELAY_MS = 250; // 강의 v2 라벨 고르기(LabelPicker)와 같은 박자

function LabelEditor({
  items = [],
  onRemove,
  inputValue = '',
  onInputChange,
  onCommit,
  suggest = null,
  onPick = null,
  busy = false,
  emptyText = '없음',
  placeholder = '라벨 추가 — Enter 또는 콤마로 구분',
}) {
  // 후보는 마지막 콤마 뒤의 글자로 찾는다 — 여러 개를 콤마로 이어 치는 중이기 때문이다
  const query = String(inputValue).split(',').pop().trim();
  const [found, setFound] = useState({ q: '', status: 'idle', items: [] });
  const seqRef = useRef(0);

  useEffect(() => {
    if (!suggest || !query) {
      setFound({ q: '', status: 'idle', items: [] });
      return undefined;
    }
    const seq = (seqRef.current += 1);
    const timer = setTimeout(() => {
      setFound((f) => ({ ...f, status: 'loading' }));
      Promise.resolve(suggest(query))
        .then((list) => {
          if (seq === seqRef.current) setFound({ q: query, status: 'ready', items: Array.isArray(list) ? list : [] });
        })
        .catch(() => {
          if (seq === seqRef.current) setFound({ q: query, status: 'error', items: [] });
        });
    }, SUGGEST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, suggest]);

  // 이미 칩으로 선 것은 후보에서 뺀다 — 눌러도 아무 일이 없는 줄을 내지 않는다
  const shownKeys = new Set(items.map((it) => String(it.key)));
  const shownTexts = new Set(items.map((it) => it.text));
  const candidates = found.items.filter((it) => !shownKeys.has(String(it.label_uuid)) && !shownTexts.has(it.label_name));
  const open = !!suggest && query !== '' && found.q === query && found.status !== 'idle';

  return (
    <div className="space-y-1.5">
      <div className="flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-white p-1.5">
        {items.map((it) => (it.locked ? (
          <span
            key={it.key}
            title={it.hint || '여기서 뗄 수 없는 값입니다'}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
          >
            <Lock className="size-3 shrink-0 text-gray-400" />
            {it.text}
          </span>
        ) : (
          <span
            key={it.key}
            title={it.hint || (it.isNew ? '저장하면 라벨 사전에 새로 오릅니다 — 사전에서 지우는 길은 아직 없습니다' : undefined)}
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-700"
          >
            {it.isNew && <span className="text-[10px] font-medium text-sky-500">새</span>}
            {it.text}
            <button
              type="button"
              onClick={() => onRemove?.(it.key)}
              disabled={busy}
              aria-label={`라벨 ${it.text} 제거`}
              className="text-sky-400 hover:text-sky-700 disabled:opacity-40"
            >
              ×
            </button>
          </span>
        )))}
        {items.length === 0 && <span className="text-xs text-gray-400">{emptyText}</span>}
      </div>
      <div>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => onInputChange?.(e.target.value)}
          onKeyDown={(e) => {
            // 글자가 있을 때만 ESC 를 여기서 끝낸다 — 비어 있으면 창 닫기로 흘려보낸다(ConceptPicker 와 같은 규칙)
            if (e.key === 'Escape' && inputValue !== '') { onInputChange?.(''); e.stopPropagation(); return; }
            // 한글 조합 중의 Enter 는 글자 확정이다 — 넣기로 읽으면 마지막 글자가 두 번 들어간다
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) { e.preventDefault(); onCommit?.(); }
          }}
          aria-label="라벨 입력"
          placeholder={placeholder}
          disabled={busy}
          className="w-full rounded-lg border px-2 py-1.5 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
        />
        {/* 후보 목록은 **흐름 안에** 둔다(열린 동안 아래 줄을 밀어낸다). 실측 2026-09-13(문제 편집 · 1536px):
            ConceptPicker 처럼 absolute z-10 으로 띄웠더니 스크롤 칸 안에서 뒤따르는 [본문] 구역 밑에 깔려 눌리지 않았다 —
            목록의 z 를 50 으로 올려도, 이 부품 뿌리에 층(relative z-20)을 줘도 그대로였고, 흐름 안에 두었을 때만 늘 위에 섰다.
            검수 창의 옛 개념 목록도 흐름 안이었다. 층 숫자로 버티면 다른 자리에서 또 깔린다. */}
        {open && (
          <div data-label-suggest className="mt-1 max-h-44 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
            {found.status === 'error' ? (
              <div className="p-2 text-xs text-amber-600">라벨 사전을 불러오지 못했습니다 — Enter 로 입력한 그대로 넣을 수 있습니다.</div>
            ) : candidates.length === 0 ? (
              <div className="p-2 text-xs text-gray-400">사전에 없는 이름입니다 — Enter 로 새 라벨로 넣습니다.</div>
            ) : candidates.map((it) => (
              <button
                key={it.label_uuid}
                type="button"
                onClick={() => onPick?.(it)}
                disabled={busy}
                className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-xs text-gray-700 transition-colors hover:bg-primary/10 disabled:opacity-40"
              >
                <span className="min-w-0 truncate">{it.label_name}</span>
                {it.problem_count != null && (
                  <span className="ml-auto shrink-0 text-[10px] text-gray-400">{it.problem_count}문제</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default LabelEditor;
